"use client";

import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock3, Sparkles } from "lucide-react";
import { FadeInSection } from "@/components/animations/FadeIn";
import { ScheduleEventCard } from "@/components/schedule/ScheduleEventCard";
import { SectionBackdrop } from "@/components/sections/SectionBackdrop";
import { useLocale } from "@/lib/locale/locale-provider";
import type { ScheduleSectionProps } from "@/lib/sections/schedule.types";
import styles from "@/components/schedule/schedule.module.css";

function buildEventKey(
  title: string,
  time: string,
  date: string | undefined,
  index: number
) {
  return [title, time, date ?? "no-date", index]
    .join("-")
    .toLowerCase()
    .replace(/\s+/g, "-");
}

export function ScheduleSection({
  title,
  subtitle,
  items,
}: ScheduleSectionProps) {
  const locale = useLocale();
  const prefersReducedMotion = useReducedMotion();
  const eyebrow =
    locale === "fr" ? "Programme du mariage" : locale === "en" ? "Wedding timeline" : "Tagesablauf";
  const summaryLabel =
    locale === "fr"
      ? `${items.length} moment(s)`
      : locale === "en"
        ? `${items.length} event(s)`
        : `${items.length} Programmpunkte`;

  const timelineItems = useMemo(
    () =>
      items.map((item, index) => ({
        id: buildEventKey(item.title, item.time, item.date, index),
        item,
        index,
        align: index % 2 === 0 ? ("left" as const) : ("right" as const),
      })),
    [items]
  );

  return (
    <SectionBackdrop tone="schedule">
      <div className={styles.inner}>
        <FadeInSection className={styles.header}>
          <p className={styles.eyebrow}>
            <Sparkles size={16} aria-hidden="true" />
            <span>{eyebrow}</span>
          </p>

          <div className={styles.headerText}>
            <h2 className={styles.title}>{title}</h2>
            {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
          </div>

          <div className={styles.summary}>
            <Clock3 size={16} aria-hidden="true" />
            <span>{summaryLabel}</span>
          </div>
        </FadeInSection>

        <div className={styles.timeline}>
          <motion.div
            aria-hidden="true"
            className={styles.progressLine}
            initial={prefersReducedMotion ? false : { opacity: 0.35, scaleY: 0 }}
            whileInView={prefersReducedMotion ? undefined : { opacity: 1, scaleY: 1 }}
            viewport={{ once: true, amount: 0.12 }}
            transition={
              prefersReducedMotion
                ? undefined
                : {
                    duration: 0.9,
                    ease: [0.22, 1, 0.36, 1],
                  }
            }
          />

          {timelineItems.map(({ id, item, index, align }) => (
            <div
              key={id}
              className={`${styles.timelineItem} ${
                align === "right" ? styles.timelineItemRight : ""
              }`}
            >
              <div
                className={`${styles.cardSlot} ${
                  align === "right" ? styles.cardSlotRight : styles.cardSlotLeft
                }`}
              >
                <ScheduleEventCard item={item} index={index} align={align} />
              </div>

              <div className={styles.marker} aria-hidden="true">
                <span className={styles.markerInner}>
                  {String(index + 1).padStart(2, "0")}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionBackdrop>
  );
}
